import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs';
import { GoalsHelper } from 'src/app/helpers/goals.helper';
import { IResult, Result } from 'src/app/models/general/result.model';
import { GoalPeriod } from 'src/app/models/goalperiod.model';
import { GoalService } from 'src/app/services';
import { ErrorMessageService } from 'src/app/shared/services';

@UntilDestroy()
@Component({
  selector: 'app-goal-basic-settings',
  templateUrl: './goal-basic-settings.component.html',
  styleUrl: './goal-basic-settings.component.scss'
})
export class GoalBasicSettingsComponent implements OnInit {

  public mainForm: UntypedFormGroup;
  public data: GoalPeriod;
  public isError = false;
  public errorMessage: string;

  public currencyCodes = GoalsHelper.currencyList;

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private goalService: GoalService,
    private msgService: ErrorMessageService,
    private translate: TranslateService
  ) {
  
    
  }

   ngOnInit(): void {
    this.mainForm = this.createForm();

    this.route.params
      .pipe(
          switchMap(params => {
              let goalPeriodId = params['goalPeriodId'];
                  
              return this.goalService.getGoalPeriodSettings(goalPeriodId);
          }),
          untilDestroyed(this)
      )
      .subscribe({ 
          next: (result) => this.handleLoadSuccess(result), 
          error: (err) => this.handleLoadError(err)
      });
  }

  private createForm(): UntypedFormGroup {
    return this.fb.group({
      'baseAmount': 0,
      'currencyCode': 'HUF',

      'numericCustomWeight': null,
      'textBasedCustomWeight': null,
      'competencyWeight': null,
    });
  }

  handleLoadSuccess(result: Result<GoalPeriod>) {
      this.data = result.data;

      this.mainForm.patchValue({
        baseAmount: this.data.baseAmount,
        currencyCode: this.data.currencyCode,
        numericCustomWeight: this.data.numericCustomWeight,
        textBasedCustomWeight: this.data.textBasedCustomWeight,
        competencyWeight: this.data.competencyWeight
      });
  }

  handleLoadError(err: IResult<GoalPeriod, Result<GoalPeriod>>) {
          this.isError = true;
          if (err?.error?.errorData?.key)
          {
              const msg = this.msgService.getErrorMessage(err.error.errorData.key);
              this.errorMessage = this.translate.instant(msg.translationKey);
          }
      }

  readFields(): GoalPeriod {
    let formData = this.mainForm.value;

    let model = this.data;
    // update fields
    model.baseAmount = formData.baseAmount ? formData.baseAmount : null;
    model.currencyCode = formData.currencyCode;

    model.numericCustomWeight = formData.numericCustomWeight ? formData.numericCustomWeight : null;
    model.textBasedCustomWeight = formData.textBasedCustomWeight ? formData.textBasedCustomWeight : null;
    model.competencyWeight = formData.competencyWeight ? formData.competencyWeight : null;

    return model;
  }

  save() {
    if (this.mainForm.invalid) return;

    let model = this.readFields();

    this.goalService.updateGoalPeriodSettings(model).subscribe({
      next: (result) => {
        this.navigateBack();
      },
      error: (err: IResult<GoalPeriod, Result<GoalPeriod>>) => {
        this.msgService.handleErrorWithData<GoalPeriod, Result<GoalPeriod>>(err);
      } 
    });
  }

  cancel() {
    this.navigateBack();
  }

  navigateBack() {
    this.router.navigate(['/goals', this.data.reviewPeriod.id, this.data.employeeId]);
  }

  getBackLink() {
    // default back link
    if (!this.data) return '/';

    return `/goals/${this.data.reviewPeriod.id}/${this.data.employeeId}`;
  }
}
